import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Marquee from "react-fast-marquee";
import {
  Wifi, Tv, Globe, Server, Code, Phone, Network, BookOpen, Cable, Cpu,
  ArrowRight, Users, Building2, Briefcase, ChevronRight
} from "lucide-react";

const iconMap = {
  Wifi, Tv, Globe, Server, Code, Phone, Network, BookOpen, Cable, Cpu,
  Container: Globe,
};

const stats = [
  { value: "1M+", label: "Subscribers", icon: Users },
  { value: "100+", label: "Employees", icon: Building2 },
  { value: "11+", label: "Services", icon: Briefcase },
  { value: "3", label: "Major Brands", icon: Globe },
];

const featuredServices = [
  {
    icon: "Wifi",
    title: "Internet Service Provider",
    desc: "Pan-India broadband connectivity with nearly a million subscribers on our high-speed fiber network.",
  },
  {
    icon: "Tv",
    title: "IPTV MSO Operations",
    desc: "Licensed MSO delivering 700+ channels to 100,000+ homes with HD and 4K streaming capabilities.",
  },
  {
    icon: "Code",
    title: "Web & App Development",
    desc: "Full-stack digital solutions from design to deployment for businesses of every scale.",
  },
  {
    icon: "Network",
    title: "MPLS Connectivity",
    desc: "Enterprise-grade MPLS networks with guaranteed QoS for mission-critical operations.",
  },
  {
    icon: "Server",
    title: "Website Hosting",
    desc: "Managed hosting with 99.9% uptime, SSL, CDN, and 24/7 technical support.",
  },
  {
    icon: "Cpu",
    title: "System Integration",
    desc: "End-to-end integration of telecom, IT infrastructure, and smart building solutions.",
  },
];

const brands = [
  {
    name: "ANT Broadband",
    url: "https://www.antbroadband.com",
    desc: "High-speed fiber broadband for homes and businesses",
    color: "#0A192F",
    logo: "/images/ant-broadband-logo.png",
  },
  {
    name: "A Digital",
    url: "https://www.adgital.tv",
    desc: "Digital content delivery and IPTV platform",
    color: "#EA580C",
    logo: "/images/a-digital-logo.png",
  },
  {
    name: "ANT TV",
    url: "#",
    desc: "Premium television and entertainment services",
    color: "#0A192F",
    logo: "/images/ant-tv-logo.png",
  },
];

function useInView(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function AnimatedCounter({ value }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  return (
    <span ref={ref} className={`${visible ? "animate-count-up" : "opacity-0"}`}>
      {value}
    </span>
  );
}

export default function Home() {
  const statsRef = useRef(null);
  const servicesRef = useRef(null);
  const brandsRef = useRef(null);
  const statsVisible = useInView(statsRef);
  const servicesVisible = useInView(servicesRef);
  const brandsVisible = useInView(brandsRef);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 animate-fade-in-up">
              Powering India's Digital Future
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1] mb-6 animate-fade-in-up delay-100 font-['Outfit']">
              Connecting a Million
              <br />
              <span className="text-[#F97316]">Across India</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-xl mb-8 animate-fade-in-up delay-200">
              From high-speed broadband and IPTV to system integration and ISP consulting &mdash;
              we deliver end-to-end telecom solutions that keep India connected.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <Link to="/services">
                <Button
                  data-testid="hero-explore-services-btn"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-8 py-6 text-sm font-semibold"
                >
                  Explore Services <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="hero-contact-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8 py-6 text-sm font-semibold"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section
        ref={statsRef}
        data-testid="stats-section"
        className="py-16 md:py-20 bg-white border-b border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  data-testid={`stat-${stat.label.toLowerCase()}`}
                  className={`text-center ${statsVisible ? "animate-fade-in-up" : "opacity-0"}`}
                  style={{ animationDelay: `${i * 120}ms` }}
                >
                  <Icon className="w-6 h-6 text-[#EA580C] mx-auto mb-3" />
                  <div className="text-3xl md:text-4xl font-bold text-[#0A192F] font-['Outfit'] mb-1">
                    <AnimatedCounter value={stat.value} />
                  </div>
                  <p className="text-sm text-[#64748B]">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section data-testid="about-teaser-section" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
                Who We Are
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-6">
                Building India's Digital
                <br />Infrastructure
              </h2>
              <p className="text-base leading-relaxed text-[#334155] mb-6">
                Assistive Netspeed Technologies Private Limited is a Bengaluru-based conglomerate
                operating across India's telecom and digital services landscape. With nearly a million
                broadband subscribers and over 100,000 IPTV users, we are among India's fast-growing
                ISP and MSO operators.
              </p>
              <p className="text-base leading-relaxed text-[#334155] mb-8">
                Through our brands &mdash; ANT Broadband, A Digital, and ANT TV &mdash; we provide
                comprehensive connectivity, entertainment, and digital transformation solutions.
              </p>
              <Link to="/about">
                <Button
                  data-testid="about-learn-more-btn"
                  variant="outline"
                  className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white rounded-sm px-6"
                >
                  Learn More <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1553601581-8a1f1010efbe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGZhY2FkZXxlbnwwfHx8fDE3NzQ4NjQ5NTl8MA&ixlib=rb-4.1.0&q=85"
                alt="Assistive Netspeed office"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#EA580C] flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white font-['Outfit']">100+</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80">Employees</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section
        ref={servicesRef}
        data-testid="services-preview-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
                What We Do
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit']">
                Our Core Services
              </h2>
            </div>
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                variant="outline"
                className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white rounded-sm"
              >
                View All Services <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredServices.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Globe;
              return (
                <Card
                  key={svc.title}
                  data-testid={`service-card-${i}`}
                  className={`service-card-accent p-6 bg-white border border-[#E2E8F0] shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 rounded-none ${
                    servicesVisible ? "animate-fade-in-up" : "opacity-0"
                  }`}
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-11 h-11 bg-[#0A192F] rounded-sm flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#EA580C]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A192F] font-['Outfit'] mb-2">
                    {svc.title}
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">{svc.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands Showcase */}
      <section
        ref={brandsRef}
        data-testid="brands-section"
        className="py-24 md:py-32 bg-[#0A192F]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
              Our Brands
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white font-['Outfit']">
              Three Brands, One Mission
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {brands.map((brand, i) => (
              <a
                key={brand.name}
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`brand-card-${brand.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`group p-8 border border-slate-700 hover:border-[#EA580C] transition-all duration-300 hover:-translate-y-1 ${
                  brandsVisible ? "animate-fade-in-up" : "opacity-0"
                }`}
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <div className="w-16 h-16 rounded-sm flex items-center justify-center mb-5 bg-white p-1.5">
                  <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                </div>
                <h3 className="text-xl font-semibold text-white font-['Outfit'] mb-2 group-hover:text-[#EA580C] transition-colors">
                  {brand.name}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{brand.desc}</p>
              </a>
            ))}
          </div>

          <Marquee speed={40} gradient={false} className="py-4">
            {["Internet Service Provider", "IPTV Solutions", "Fiber Broadband", "Web Hosting", "System Integration", "MPLS Networks", "IVR Solutions", "Web Development", "ISP Consulting", "Telecom Equipment"].map((item) => (
              <span
                key={item}
                className="mx-8 text-sm font-medium uppercase tracking-[0.15em] text-slate-500"
              >
                {item} <span className="text-[#EA580C] mx-4">/</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Ready to Connect?
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-6 max-w-2xl mx-auto">
            Let's Power Your Business
            <br />With Reliable Connectivity
          </h2>
          <p className="text-base text-[#64748B] mb-8 max-w-lg mx-auto">
            Whether you need broadband, IPTV, or enterprise networking &mdash; we have the
            infrastructure and expertise to deliver.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-8 py-6 text-sm font-semibold"
              >
                Get in Touch <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                data-testid="cta-services-btn"
                variant="outline"
                className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white rounded-sm px-8 py-6 text-sm font-semibold"
              >
                Browse Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
